import { ApiError } from "./api/http";

/**
 * What to tell a person when a call fails. Codes the API or Better Auth explain well enough keep the server's
 * message; the ones below get a sentence that says what to do next.
 */
const MESSAGES: Record<string, string> = {
  NETWORK: "Can't reach the server. Check your connection and try again.",
  UNAUTHENTICATED: "Your session has ended. Sign in again.",
  FORBIDDEN_ROLE: "Your role in this workspace doesn't allow this. Ask an owner or admin.",
  READ_ONLY_WORKSPACE: "This workspace has no active plan, so it is read-only. Choose a plan under billing.",
  NOT_A_MEMBER: "That workspace doesn't exist or you're not a member of it.",
  INTERNAL: "Something went wrong on our side. Try again in a moment.",

  // Better Auth
  INVALID_EMAIL_OR_PASSWORD: "That email and password don't match an account.",
  EMAIL_NOT_VERIFIED: "Confirm your email first. We've sent you a code.",
  USER_ALREADY_EXISTS: "There's already an account with this email. Sign in instead.",
  USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL: "There's already an account with this email. Sign in instead.",
  TERMS_OUTDATED: "The Terms changed while this page was open. Read the latest version, then accept again.",
  TERMS_NOT_ACCEPTED: "Tick the box to accept the Terms of Service and Privacy Policy.",
  PASSWORD_TOO_SHORT: "Use at least 10 characters for your password.",
  INVALID_OTP: "That code isn't right. Check the latest email and try again.",
  OTP_EXPIRED: "That code has expired. Send a new one.",
  TOO_MANY_ATTEMPTS: "Too many wrong codes. Send a new one.",

  // videos
  UNSUPPORTED_CONTENT_TYPE: "That file type isn't supported. Use MP4, MOV, MKV, WebM, AVI, MPEG or TS.",
  QUOTA_STORAGE_EXCEEDED: "This upload would go over your storage. Delete some videos or upgrade your plan.",
  QUOTA_FILES_EXCEEDED: "You've reached your plan's file limit. Delete some videos or upgrade your plan.",
  VIDEO_IN_USE: "A stream's playlist still uses this video. Remove it from the playlist first.",

  // streams
  STREAM_NOT_EDITABLE: "Stop the stream before changing it.",
  ORIENTATION_MISMATCH: "All videos in a playlist need the same orientation (all landscape or all portrait).",
  QUALITY_NOT_ALLOWED: "Your plan or this platform doesn't allow that quality.",
  FPS_NOT_ALLOWED: "Your plan or this platform doesn't allow that frame rate.",
  SCHEDULE_ENDED: "The scheduled end time has passed. Change or clear it, then start again.",
  RTMP_URL_NOT_ALLOWED: "That RTMP address isn't allowed. Use a public server address.",

  // members (refusals from our Better Auth organization hooks)
  MEMBER_LIMIT_REACHED: "Every seat on your plan is taken (pending invites count too). Upgrade the plan to invite more people.",

  // billing
  BILLING_DISABLED: "Payments aren't set up yet.",
  BILLING_PROVIDER_ERROR: "The payment provider didn't respond. Try again in a moment.",
  NO_BILLING_ACCOUNT: "This workspace hasn't had a paid plan yet, so there's nothing to manage.",
};

export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.code === "RATE_LIMITED" || e.status === 429) return "Too many tries. Wait a minute, then try again.";
    return MESSAGES[e.code] ?? e.message;
  }
  if (e instanceof Error && e.name === "AbortError") return "Cancelled.";
  return e instanceof Error ? e.message : "Something went wrong.";
}
